<template>
	<div v-if="fruitBg.isOn">
		<canvas
			data-testid="fruit-canvas"
			ref="canvasRef"
			class="fruit-rain-canvas pointer-events-none fixed inset-0" />
	</div>
</template>

<script setup>
	import { ref, onMounted, onUnmounted, watch, computed, nextTick } from "vue"
	import { useFruitBackground } from "@/stores/fruit-background"

	const fruitBg = useFruitBackground()
	fruitBg.load()

	const props = defineProps({
		density: { type: Number, default: 28 },
		showGradient: { type: Boolean, default: false },
	})

	const canvasRef = ref(null)
	const requestIdRef = ref(0)
	const bodies = ref([])
	const fruits = ["🍎", "🍌", "🍊", "🍓", "🍇", "🥝", "🥑", "🥕"]

	function initializeBodies(width, height) {
		bodies.value = []
		for (let i = 0; i < props.density; i++) {
			bodies.value.push({
				x: Math.random() * width,
				y: Math.random() * height,
				s: 16 + Math.random() * 20,
				v: 0.6 + Math.random() * 1.2,
				r: Math.random() * Math.PI,
				vr: (Math.random() - 0.5) * 0.01,
				emoji: fruits[(Math.random() * fruits.length) | 0],
			})
		}
	}

	function resizeCanvas() {
		const canvas = canvasRef.value
		if (!canvas) return
		const { innerWidth: w, innerHeight: h } = window
		canvas.width = w
		canvas.height = h
		if (bodies.value.length === 0) {
			initializeBodies(w, h)
		}
	}

	function tick() {
		const canvas = canvasRef.value
		if (!canvas) return
		const ctx = canvas.getContext("2d")
		if (!ctx) return

		const { width: w, height: h } = canvas
		ctx.clearRect(0, 0, w, h)

		// subtle vignette gradient behind fruit (optional)
		if (props.showGradient) {
			const g = ctx.createRadialGradient(
				w * 0.5,
				h * 0.3,
				0,
				w * 0.5,
				h * 0.3,
				Math.max(w, h)
			)
			g.addColorStop(0, "rgba(0,0,0,0.0)")
			g.addColorStop(1, "rgba(0,0,0,0.25)")
			ctx.fillStyle = g
			ctx.fillRect(0, 0, w, h)
		}

		ctx.textAlign = "center"
		ctx.textBaseline = "middle"
		bodies.value.forEach((b) => {
			b.y += b.v
			b.r += b.vr
			b.x += Math.sin(b.y * 0.01) * 0.3
			if (b.y - b.s > h) {
				b.y = -20
				b.x = Math.random() * w
			}
			ctx.save()
			ctx.translate(b.x, b.y)
			ctx.rotate(b.r)
			ctx.font = `${b.s}px system-ui, "Apple Color Emoji", "Segoe UI Emoji"`
			ctx.globalAlpha = 0.9
			ctx.fillText(b.emoji, 0, 0)
			ctx.restore()
		})

		requestIdRef.value = requestAnimationFrame(tick)
	}

	function startFruitRain() {
		resizeCanvas()
		window.addEventListener("resize", resizeCanvas)
		requestIdRef.value = requestAnimationFrame(tick)
	}

	function stopFruitRain() {
		window.removeEventListener("resize", resizeCanvas)
		if (requestIdRef.value) cancelAnimationFrame(requestIdRef.value)
		const canvas = canvasRef.value
		if (canvas) {
			const ctx = canvas.getContext("2d")
			if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
		}
	}

	onMounted(() => {
		if (fruitBg.isOn) {
			startFruitRain()
		}
	})

	onUnmounted(() => {
		stopFruitRain()
	})

	watch(
		() => fruitBg.isOn,
		(isOn) => {
			if (isOn) {
				nextTick(() => {
					resizeCanvas()
					startFruitRain()
				})
			} else {
				stopFruitRain()
			}
		}
	)

	// If density changes at runtime, reinitialize bodies next resize
	watch(
		() => props.density,
		() => {
			const canvas = canvasRef.value
			const w = canvas ? canvas.width : window.innerWidth
			const h = canvas ? canvas.height : window.innerHeight
			initializeBodies(w, h)
		}
	)
</script>

<style scoped>
	.fruit-rain-canvas {
		position: fixed;
		inset: 0;
		z-index: -1; /* keep behind everything */
		pointer-events: none;
	}

	.veil {
		position: fixed;
		inset: -20vmax;
		z-index: 1;
		pointer-events: none;
		background: conic-gradient(
			from 0deg at 50% 50%,
			#0000 0 20%,
			#0a2f1a22 20% 40%,
			#0000 40% 60%,
			#245c2d22 60% 80%,
			#0000 80%
		);
		animation: spin 30s linear infinite;
		mix-blend-mode: overlay;
	}
	@keyframes spin {
		to {
			transform: rotate(360deg);
		}
	}
</style>
